import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";

import useShiftLockInfo from "../../components/Hooks/useShiftLockInfo";
import { useTimezone } from "../../components/Providers/TimezoneProvider";
import { shiftDurationHours, shiftStartHour } from "../../offline/shiftBands";
import { TripleShift, shiftNameToLetter } from "../../types/Shift";
import { resolveLiveHourIndex, shiftHourSlots } from "../../utilities/helpers/shiftHourHelpers";
import { isCurrentShift } from "../../utilities/helpers/shiftLockStateHelpers";
import { movementShiftWindow } from "./movementEntry";

export function useMovementTiming(selectedDate: DateTime, shift: TripleShift) {
  const { timezone } = useTimezone();
  const zone = timezone ?? "UTC";
  const { shiftLockState } = useShiftLockInfo();

  const hours = useMemo(() => shiftHourSlots(shiftStartHour(shift), shiftDurationHours(shift)), [shift]);
  const shiftWindow = useMemo(
    () => movementShiftWindow(selectedDate.toISODate()!, shift, zone),
    [selectedDate, shift, zone]
  );

  const isLatestShift = useMemo(
    () => isCurrentShift(selectedDate, shiftNameToLetter(shift), shiftLockState),
    [selectedDate, shift, shiftLockState]
  );

  const [now, setNow] = useState(() => DateTime.now());
  useEffect(() => {
    if (!isLatestShift) return;
    const id = setInterval(() => setNow(DateTime.now()), 30_000);
    return () => clearInterval(id);
  }, [isLatestShift]);

  const liveHourIndex = resolveLiveHourIndex(hours, now.setZone(zone).hour, isLatestShift);

  return { zone, hours, liveHourIndex, shiftWindow };
}
